// dwaar/src/pages/Aavahan.jsx
// Repo: terralekha-core | Folder: dwaar/src/pages/
// Aavahan — Sanskrit आवाहन — invitation.
//   Sign-in page: role selector (left) + OTP form (right).
//   Two auth paths:
//     "Mobile OTP"  (primary) — phone number → OTP via Sandesha → action_link → Saakshi
//     "Email link"  (fallback) — email → magic link via Pravesh → Saakshi
//
// Reveal doctrine:
//   - All errors render only PUBLIC_ERROR; upstream detail stays in console.
//   - Confirmation copy is generic (no enumeration leakage).

import React, { useState } from "react";

import { requestMagicLink, sendOtp, verifyOtp } from "../lib/praveshClient.js";
import { navigateTo } from "../lib/nav.js";
import { BRAND_NAME, PUBLIC_ERROR } from "../lib/brand.js";

// ── Phase enumerations ────────────────────────────────────────────────────────

const MOBILE_PHASE = Object.freeze({
  IDLE:      "idle",
  SENDING:   "sending",
  OTP_SENT:  "otp_sent",
  VERIFYING: "verifying",
  ERROR:     "error",
});

const EMAIL_PHASE = Object.freeze({
  IDLE:    "idle",
  SENDING: "sending",
  SENT:    "sent",
  ERROR:   "error",
});

// ── Tab IDs ───────────────────────────────────────────────────────────────────

const TAB = Object.freeze({ MOBILE: "mobile", EMAIL: "email" });

// ── Role → audience map (matches landing page openModal keys) ────────────────
const ROLE_AUDIENCE = { msme: "exporter", auditor: "auditor", ca: "consultant", sebi: "brsr" };

// ── Role definitions ──────────────────────────────────────────────────────────

const ROLES = [
  {
    id: "msme",
    name: "MSME Exporter",
    color: "#C94060",
    sub: "Generate your CBAM PassPort for EU shipments",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 9l9-6 9 6v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
        <path d="M9 22V12h6v10"/>
      </svg>
    ),
  },
  {
    id: "auditor",
    name: "ISO Auditor",
    color: "#3A7A4A",
    sub: "Verify and co-sign under ISO 14065:2020",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M9 12l2 2 4-4"/>
        <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9c2.5 0 4.78.99 6.43 2.6"/>
      </svg>
    ),
  },
  {
    id: "ca",
    name: "CA Partner",
    color: "#6A4E10",
    sub: "Manage all your client accounts in one place",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
        <path d="M14 2v6h6"/><path d="M9 13h6M9 17h4"/>
      </svg>
    ),
  },
  {
    id: "sebi",
    name: "SEBI ESG",
    color: "#2A5C8A",
    sub: "BRSR-aligned ESG disclosures & filings",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="2"/>
        <path d="M7 12l3 3 7-7"/>
      </svg>
    ),
  },
];

// ── Mobile OTP sub-component ──────────────────────────────────────────────────

function MobileOtpPanel() {
  const [phase,    setPhase]    = useState(MOBILE_PHASE.IDLE);
  const [digits,   setDigits]   = useState("");
  const [otp,      setOtp]      = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  function e164Mobile() {
    const clean = digits.replace(/\D/g, "");
    return `+91${clean}`;
  }

  async function handleSend(e) {
    e.preventDefault();
    setErrorMsg("");
    setPhase(MOBILE_PHASE.SENDING);
    try {
      await sendOtp(e164Mobile());
      setPhase(MOBILE_PHASE.OTP_SENT);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("sendOtp failed:", err);
      setErrorMsg(PUBLIC_ERROR);
      setPhase(MOBILE_PHASE.ERROR);
    }
  }

  async function handleVerify(e) {
    e.preventDefault();
    setErrorMsg("");
    setPhase(MOBILE_PHASE.VERIFYING);
    try {
      const result = await verifyOtp(e164Mobile(), otp);
      navigateTo(result.action_link);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("verifyOtp failed:", err);
      setErrorMsg(PUBLIC_ERROR);
      setPhase(MOBILE_PHASE.OTP_SENT);
    }
  }

  function handleResend() {
    setOtp("");
    setErrorMsg("");
    setPhase(MOBILE_PHASE.IDLE);
  }

  const isSending   = phase === MOBILE_PHASE.SENDING;
  const isVerifying = phase === MOBILE_PHASE.VERIFYING;
  const inOtpPhase  = phase === MOBILE_PHASE.OTP_SENT   ||
                      phase === MOBILE_PHASE.VERIFYING  ||
                      (phase === MOBILE_PHASE.ERROR && otp !== "");

  const showSendForm   = !inOtpPhase;
  const showVerifyForm = inOtpPhase;

  return (
    <>
      {showSendForm && (
        <form onSubmit={handleSend} noValidate>
          <div className="tl-phone-row">
            <span className="tl-phone-prefix" aria-hidden="true">+91</span>
            <input
              id="aavahan-mobile"
              className="tl-input tl-input--phone"
              type="tel"
              name="mobile"
              autoComplete="tel-national"
              required
              inputMode="numeric"
              maxLength={10}
              placeholder="98765 41234"
              value={digits}
              onChange={(e) => setDigits(e.target.value.replace(/\D/g, "").slice(0, 10))}
              disabled={isSending}
              aria-label="Mobile number"
            />
          </div>

          <button
            className="tl-button"
            type="submit"
            disabled={isSending || digits.replace(/\D/g, "").length < 10}
          >
            {isSending ? "Sending…" : "Send OTP"}
          </button>

          {phase === MOBILE_PHASE.ERROR && errorMsg && (
            <p className="tl-error" role="alert">{errorMsg}</p>
          )}
        </form>
      )}

      {showVerifyForm && (
        <form onSubmit={handleVerify} noValidate>
          <p className="tl-sub tl-otp-hint">
            OTP sent to +91&thinsp;{digits}. Enter it below.
          </p>

          <label className="tl-label" htmlFor="aavahan-otp">
            One-time password
          </label>
          <input
            id="aavahan-otp"
            className="tl-input tl-input--otp"
            type="text"
            name="otp"
            autoComplete="one-time-code"
            inputMode="numeric"
            required
            maxLength={6}
            placeholder="123456"
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
            disabled={isVerifying}
            aria-label="OTP"
          />

          <button
            className="tl-button"
            type="submit"
            disabled={isVerifying || otp.length < 4}
          >
            {isVerifying ? "Verifying…" : "Verify"}
          </button>

          <button
            type="button"
            className="tl-link-btn"
            onClick={handleResend}
            disabled={isVerifying}
            aria-label="Resend OTP"
          >
            Resend OTP
          </button>

          {errorMsg && (
            <p className="tl-error" role="alert">{errorMsg}</p>
          )}
        </form>
      )}
    </>
  );
}

// ── Email Link sub-component ──────────────────────────────────────────────────

function EmailLinkPanel() {
  const [email, setEmail] = useState("");
  const [phase, setPhase] = useState(EMAIL_PHASE.IDLE);

  async function handleSubmit(e) {
    e.preventDefault();
    const form = e.currentTarget;
    if (!form.checkValidity()) {
      form.reportValidity?.();
      return;
    }
    setPhase(EMAIL_PHASE.SENDING);
    try {
      await requestMagicLink(email.trim());
      setPhase(EMAIL_PHASE.SENT);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("requestMagicLink failed:", err);
      setPhase(EMAIL_PHASE.ERROR);
    }
  }

  const isSending = phase === EMAIL_PHASE.SENDING;

  if (phase === EMAIL_PHASE.SENT) {
    return (
      <>
        <h2 className="tl-heading tl-heading--sm">Check your email</h2>
        <p className="tl-sub">
          If this address is registered, a sign-in link is on the way. It
          expires in one hour. You can close this tab.
        </p>
      </>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate={false}>
      <label className="tl-label" htmlFor="aavahan-email">
        Email address
      </label>
      <input
        id="aavahan-email"
        className="tl-input"
        type="email"
        name="email"
        autoComplete="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        disabled={isSending}
        aria-label="Email"
      />

      <button
        className="tl-button"
        type="submit"
        disabled={isSending}
      >
        {isSending ? "Sending…" : "Send magic link"}
      </button>

      {phase === EMAIL_PHASE.ERROR && (
        <p className="tl-error" role="alert">{PUBLIC_ERROR}</p>
      )}
    </form>
  );
}

// ── Aavahan (main component) ──────────────────────────────────────────────────

export default function Aavahan() {
  const [activeTab,    setActiveTab]    = useState(TAB.MOBILE);
  const [selectedRole, setSelectedRole] = useState(ROLES[0]);

  return (
    <div className="tl-si-page">

      {/* ── Topbar ── */}
      <div className="tl-si-topbar">
        <a href="https://terralekha.in" className="tl-si-brand">
          {/* Logo matches landing page nav exactly */}
          <div className="tl-si-brand-logo" aria-hidden="true">
            <svg width="34" height="34" viewBox="0 0 32 32" fill="none">
              <rect width="32" height="32" rx="8" fill="rgba(26,92,56,0.12)"/>
              <path d="M16 6C11 6 7 10.5 7 16.5C7 21.5 10.5 25.2 15 26C15 26 14 21 17 18C20 15 24 15.5 24 15.5C24 15.5 23.5 10 16 6Z" fill="rgba(26,92,56,0.78)"/>
              <path d="M15 26C15.5 22 16.5 19 20 17" stroke="rgba(139,96,16,0.5)" strokeWidth="0.8" strokeLinecap="round"/>
              <line x1="9" y1="20" x2="21" y2="20" stroke="rgba(26,92,56,0.22)" strokeWidth="0.5"/>
              <line x1="8" y1="23" x2="20" y2="23" stroke="rgba(26,92,56,0.15)" strokeWidth="0.5"/>
            </svg>
          </div>
          <div>
            <div className="tl-si-brand-wordmark">{BRAND_NAME}</div>
            <div className="tl-si-brand-tagline">Earth&apos;s Record. Faithfully kept.</div>
          </div>
        </a>
        <div className="tl-si-topbar-help">
          <a href="mailto:support@terralekha.in" aria-label="Email support team">support@terralekha.in</a>
        </div>
      </div>

      {/* ── Shell ── */}
      <div className="tl-si-shell">

        {/* Intro */}
        <div className="tl-si-intro">
          <p className="tl-si-eyebrow">Sign in</p>
          <h1 className="tl-si-h1" id="aavahan-heading">
            Welcome back to <em>your ledger.</em>
          </h1>
        </div>

        {/* Two-column grid */}
        <div className="tl-si-grid">

          {/* ── LEFT: Role chooser ── */}
          <div className="tl-si-panel">
            <div className="tl-si-panel-label">
              <div className="tl-si-panel-num">1</div>
              <div className="tl-si-panel-title">Who&apos;s signing in today?</div>
            </div>

            <div className="tl-si-roles">
              {ROLES.map((role) => (
                <button
                  key={role.id}
                  type="button"
                  aria-label={role.name}
                  aria-pressed={selectedRole.id === role.id}
                  className={`tl-si-role-card${selectedRole.id === role.id ? " selected" : ""}`}
                  style={{ "--c": role.color }}
                  onClick={() => setSelectedRole(role)}
                >
                  <div className="tl-si-role-icon" style={{ color: role.color }}>
                    {role.icon}
                  </div>
                  <div className="tl-si-role-text">
                    <div className="tl-si-role-name">{role.name}</div>
                    <div className="tl-si-role-sub">{role.sub}</div>
                  </div>
                  <div className="tl-si-role-check">
                    {selectedRole.id === role.id && "✓"}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* ── RIGHT: OTP sign-in ── */}
          <div className="tl-si-panel">
            <div className="tl-si-panel-label">
              <div className="tl-si-panel-num">2</div>
              <div className="tl-si-panel-title">Sign in to your workspace</div>
            </div>

            {/* Selected role summary */}
            <div className="tl-si-summary">
              <div className="tl-si-summary-icon" style={{ color: selectedRole.color }}>
                {selectedRole.icon}
              </div>
              <div>
                <div className="tl-si-summary-label">Signing in as</div>
                <div className="tl-si-summary-name">{selectedRole.name}</div>
              </div>
            </div>

            {/* OTP tab bar */}
            <div className="tl-si-tab-bar" role="tablist" aria-label="Sign-in method">
              <button
                role="tab"
                id="tab-mobile"
                aria-controls="panel-mobile"
                aria-selected={activeTab === TAB.MOBILE}
                className={`tl-si-tab${activeTab === TAB.MOBILE ? " tl-si-tab--active" : ""}`}
                onClick={() => setActiveTab(TAB.MOBILE)}
              >
                Mobile OTP
              </button>
              <button
                role="tab"
                id="tab-email"
                aria-controls="panel-email"
                aria-selected={activeTab === TAB.EMAIL}
                className={`tl-si-tab${activeTab === TAB.EMAIL ? " tl-si-tab--active" : ""}`}
                onClick={() => setActiveTab(TAB.EMAIL)}
              >
                Email link
              </button>
            </div>

            {/* OTP panels */}
            <div className="tl-si-otp-wrap">
              <div
                id="panel-mobile"
                role="tabpanel"
                aria-labelledby="tab-mobile"
                hidden={activeTab !== TAB.MOBILE}
                className="tl-tab-panel"
              >
                {activeTab === TAB.MOBILE && <MobileOtpPanel />}
              </div>
              <div
                id="panel-email"
                role="tabpanel"
                aria-labelledby="tab-email"
                hidden={activeTab !== TAB.EMAIL}
                className="tl-tab-panel"
              >
                {activeTab === TAB.EMAIL && <EmailLinkPanel />}
              </div>
            </div>
          </div>

        </div>

        {/* ── Signup band ── */}
        <div className="tl-si-signup-band">
          <div className="tl-si-signup-text">
            <div className="tl-si-signup-label">First time here?</div>
            <h3 className="tl-si-signup-h3">
              Your first PassPort is <em>free.</em>
            </h3>
            <p className="tl-si-signup-p">
              No credit card. No contract. See your verified emissions before you commit.
            </p>
          </div>
          <a
            href={`https://terralekha.in?register=${ROLE_AUDIENCE[selectedRole.id] || "exporter"}`}
            className="tl-si-signup-cta"
          >
            Register free — first PassPort on us
            <span className="tl-si-signup-arrow" aria-hidden="true">→</span>
          </a>
        </div>

        {/* ── Footer ── */}
        <div className="tl-si-foot">
          <span>© 2026 TERRALEKHA. All rights reserved.</span>
          <div className="tl-si-foot-links">
            <a href="https://terralekha.in/privacy.html">Privacy</a>
            <a href="https://terralekha.in/terms.html">Terms</a>
          </div>
        </div>

      </div>
    </div>
  );
}
